import type { BoardUser } from "../../../api/leaderboard/leaderboard.dto.js";
import type { LeaderboardScreenPlayerInfoConfig } from "./leaderboard_screen_player_info_config.js";
import { daysSince } from "../../../common/days_in_game.js";

/** What the current player looks like locally, ahead of the leaderboard backend. */
export interface OwnPlayerOverride
{
  id: string;
  avatar_id: number;
  facebook_av_url: string | null;
  /** Days since the account's server registration, as the own profile shows them. */
  days_in_game?: number | null;
}

/**
 * Server users → list rows, sorted by rank. Rows keep only what the list and the profile
 * dialog show; the raw response (16 fields per user) is not referenced afterwards.
 * Every row gets its own place (see `uniqueRanks`): equal scores do not share one.
 */
export function buildPlayerRows( users: BoardUser[], own: OwnPlayerOverride ): LeaderboardScreenPlayerInfoConfig[]
{
  const seen = new Set<string>();
  const rows: LeaderboardScreenPlayerInfoConfig[] = [];
  for ( const u of users )
  {
    const device = String( u.deviceId ?? '' ).trim();
    const user_id = String( u.deviceId ?? u.userId ?? '' );
    // The current player can come both inside the top and as the appended own row.
    if ( user_id && seen.has( user_id ) )
      continue;
    seen.add( user_id );
    const is_player = device.length > 0 && own.id.length > 0 && device === own.id;
    rows.push( {
      is_player,
      name:      u.name || 'Player',
      level:     u.level ?? 1,
      rating:    u.ratingPosition,
      // Own row reflects the local avatar choice (incl. FB id 987) immediately,
      // even before the leaderboard backend echoes it back.
      avatar_id: is_player ? own.avatar_id : ( u.avatar ?? 0 ),
      facebook_av_url: is_player ? own.facebook_av_url : ( u.fbUrl ?? null ),
      score:     u.ratingScore,
      country_code: u.country ?? '',
      user_id,
      // The leaderboard's createdAt is when the player joined the board, not the game: the own
      // row uses the account's date, or the profile and the board showed different days.
      days_in_game: ( is_player ? own.days_in_game : null ) ?? daysSince( u.createdAt ),
    } );
  }
  // Stable: players with an equal score keep the server order, which is the tie-break.
  rows.sort( ( a, b ) => a.rating - b.rating );
  uniqueRanks( rows );
  return rows;
}

/**
 * The server ranks densely: equal scores share a place (two players with 100 are both #29, all
 * zeros are #101). The list shows a place per player instead, in the order the rows come. The top
 * (server rank grows by at most 1 per row) is numbered 1, 2, 3...; rows after a gap are the window
 * the server appends around the player, ranked by "1 + players above", and keep that rank while it
 * stays above the previous row's.
 */
function uniqueRanks( rows: LeaderboardScreenPlayerInfoConfig[] ): void
{
  let in_top = true;
  let prev_server = 0;
  let prev_rank = 0;
  for ( const r of rows )
  {
    const server = r.rating;
    if ( in_top && server - prev_server > 1 )
      in_top = false;
    r.rating = in_top ? prev_rank + 1 : Math.max( server, prev_rank + 1 );
    prev_server = server;
    prev_rank = r.rating;
  }
}

/**
 * Change check for a silent refresh. A JSON.stringify of a 1000-row board was a ~200 KB
 * string kept per tab; an FNV-1a hash over the shown fields is a few bytes.
 */
export function playersKey( rows: LeaderboardScreenPlayerInfoConfig[] ): string
{
  let h = 0x811c9dc5;
  const mix = ( s: string ): void =>
  {
    for ( let i = 0; i < s.length; i++ )
    {
      h ^= s.charCodeAt( i );
      h = Math.imul( h, 0x01000193 );
    }
    h ^= 0x1f;
    h = Math.imul( h, 0x01000193 );
  };
  for ( const r of rows )
  {
    mix( r.user_id ?? '' );
    mix( r.name );
    mix( `${r.rating}|${r.score}|${r.level}|${r.avatar_id}|${r.is_player ? 1 : 0}|${r.days_in_game ?? ''}` );
    mix( r.facebook_av_url ?? '' );
    mix( r.country_code );
  }
  return `${rows.length}:${( h >>> 0 ).toString( 36 )}`;
}

/**
 * Board indices that need a built row at this scroll offset: the ones in the viewport plus
 * `buffer` on each side, clamped to the board. Empty board → first > last.
 */
export function visibleRowRange(
  offset: number,
  view_h: number,
  step: number,
  top_pad: number,
  count: number,
  buffer: number,
): { first: number; last: number }
{
  if ( count <= 0 || step <= 0 )
    return { first: 0, last: -1 };
  const top = offset - top_pad;
  const first = Math.min( count - 1, Math.max( 0, Math.floor( top / step ) - buffer ) );
  const last = Math.max( first, Math.min( count - 1, Math.floor( ( top + view_h ) / step ) + buffer ) );
  return { first, last };
}
